import { devLog } from '../../utils/devLog';

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { getRoleAnalyses, getCandidateProfile } from '../../utils/api';
import EmptyState from '../common/EmptyState';
import ApplicantItem from './ApplicantItem';

const CVAnalysisPanel = ({ jobId, onSelectApplicant, selectedApplicant }) => {
  const { jobs } = useAppContext();
  const [applicants, setApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const selectedJob = jobs.find(job => job.id === jobId);
  const roleId = selectedJob?.roleId;

  useEffect(() => {
    const fetchAnalyses = async () => {
      if (!roleId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await getRoleAnalyses(roleId);
        
        if (result.success && result.data) {
          // Transform API data to our format and fetch detailed profiles
          const transformedApplicants = await Promise.all(result.data.map(async (analysis, index) => {
            // Debug logging disabled
            
            // Parse the overallScore (e.g., "6.0/10" -> 6.0)
            const scoreMatch = analysis.overallScore?.match(/([0-9.]+)/);
            const overallScore = scoreMatch ? parseFloat(scoreMatch[1]) : 0;
            
            // Get section scores
            const sections = analysis.sections || {};
            const tech = sections.Technical || 0;
            const exp = sections.Experience || 0;
            const edu = sections.Education || 0;
            const skill = sections.Skills || 0;

            // Try to fetch detailed candidate profile for more complete data
            let detailedProfile = null;
            if (analysis.email) {
              try {
                const profileResult = await getCandidateProfile(roleId, analysis.email);
                if (profileResult.success) {
                  detailedProfile = profileResult.data;
                }
              } catch (err) {
                devLog.warn('Could not fetch detailed profile for', analysis.email);
              }
            }

            // Prefer detailed profile data, fallback to analysis data
            const experience = detailedProfile?.experience || 
                             (analysis.experienceYears ? `${analysis.experienceYears} years` : 
                             (analysis.experience || 'Not specified'));
            
            const education = detailedProfile?.education || analysis.education || 'Not specified';
            const phone = detailedProfile?.phone || analysis.phone || 'Not provided';
            const location = detailedProfile?.location || analysis.location || 'Not specified';

            // Extract candidateId (ensure it's not empty string)
            // Check for candidateId, then _id (MongoDB ID)
            const candidateIdFromAnalysis = (analysis.candidateId && analysis.candidateId.trim()) || (analysis._id && analysis._id.toString()) || null;
            const candidateIdFromProfile = (detailedProfile?.candidateId && detailedProfile.candidateId.trim()) || (detailedProfile?._id && detailedProfile._id.toString()) || null;
            const finalCandidateId = candidateIdFromAnalysis || candidateIdFromProfile || null;
            
            // Debug logging disabled
            
            return {
              id: finalCandidateId || analysis.email || `applicant-${index}`,
              candidateId: finalCandidateId, // Use candidate ID from analysis or profile
              email: analysis.email,
              name: analysis.name || 'Unknown Candidate',
              overall: overallScore,
              cvScore: overallScore,
              tech,
              exp,
              edu,
              skill,
              experience,
              education,
              location: location === 'N/A' ? 'Not specified' : location,
              phone,
              status: analysis.status || 'Pending',
              rank: analysis.rank || 0,
              interviewCompleted: false
            };
          }));

          setApplicants(transformedApplicants);
        } else {
          setApplicants([]);
        }
      } catch (err) {
        devLog.error('Error fetching CV analyses:', err);
        setError('Failed to load CV analyses. Please try again.');
        setApplicants([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalyses();
  }, [roleId]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '60px 20px',
        color: '#718096'
      }}>
        <div style={{ 
          fontSize: '3rem', 
          marginBottom: '20px',
          animation: 'pulse 2s infinite'
        }}>
          🔄
        </div>
        <p>Loading CV analyses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        background: '#fee', 
        border: '2px solid #fcc', 
        borderRadius: '12px', 
        padding: '20px', 
        textAlign: 'center',
        color: '#c00'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚠️</div>
        <p>{error}</p>
      </div>
    );
  }

  if (applicants.length === 0) {
    return (
      <EmptyState 
        message="No CV analyses available yet. CVs may still be processing, or no CVs have been uploaded for this job." 
        icon="📄" 
      />
    );
  }

  return (
    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
      {applicants.map((applicant) => (
        <ApplicantItem
          key={applicant.id}
          applicant={applicant}
          jobTitle={selectedJob?.title}
          isSelected={selectedApplicant?.id === applicant.id}
          onSelect={() => onSelectApplicant(applicant)}
        />
      ))}
    </div>
  );
};

export default CVAnalysisPanel;
